import {
  ASH_ADMIN,
  NON_PHI_ORGANIZATION_ADMIN,
  NON_PHI_PROGRAM_ADMIN,
  ORGANIZATION_ADMIN,
} from '@/components/client/constants/roles'
import type { IdentityInfoSchemaType } from '@/types/portal-api.generated'
import {
  isSelectableScopeKey,
  normalizeClientId,
  normalizeNullableClientId,
} from '@/utilities/auth/authHelper'

export type IdentityScopes = IdentityInfoSchemaType['scopes']
export type IdentityScopeValue = { roles?: unknown; is_primary?: unknown }

export function narrowIdentityScope(scope: IdentityScopes[string] | undefined): IdentityScopeValue {
  return typeof scope === 'object' && scope !== null ? (scope as IdentityScopeValue) : {}
}

export function getRolesFromScope(scope: IdentityScopeValue): string[] {
  return Array.isArray(scope.roles)
    ? scope.roles.filter((role): role is string => typeof role === 'string')
    : []
}

export function rolesInclude(roles: readonly string[], expectedRoles: readonly string[]): boolean {
  const normalized = new Set(roles.map((role) => role.toLowerCase()))
  return expectedRoles.some((role) => normalized.has(role.toLowerCase()))
}

export function getRolesFromScopes(scopes: IdentityScopes | null | undefined): string[] {
  if (!scopes || typeof scopes !== 'object') return []

  return Object.values(scopes).flatMap((scope) => getRolesFromScope(narrowIdentityScope(scope)))
}

export function hasAshAdminRoleInScopes(scopes: IdentityScopes | null | undefined): boolean {
  return rolesInclude(getRolesFromScopes(scopes), [ASH_ADMIN])
}

export function hasOrgAdminRoleInScopes(scopes: IdentityScopes | null | undefined): boolean {
  return rolesInclude(getRolesFromScopes(scopes), [ORGANIZATION_ADMIN, NON_PHI_ORGANIZATION_ADMIN])
}

export function findIdentityScopeByNormalizedPartnerId(
  scopes: IdentityScopes | null | undefined,
  partnerId: string | null | undefined,
): IdentityScopeValue | null {
  if (!scopes) return null
  const normalizedPartnerId = normalizeNullableClientId(partnerId)
  if (!normalizedPartnerId) return null

  for (const [scopeKey, scope] of Object.entries(scopes)) {
    if (!isSelectableScopeKey(scopeKey)) continue
    if (normalizeClientId(scopeKey) === normalizedPartnerId) {
      return narrowIdentityScope(scope)
    }
  }

  return null
}

export function hasNonPhiRole(roles: readonly string[]): boolean {
  const normalizedRoles = new Set(roles.map((role) => role.toLowerCase()))
  return (
    normalizedRoles.has(NON_PHI_ORGANIZATION_ADMIN.toLowerCase()) ||
    normalizedRoles.has(NON_PHI_PROGRAM_ADMIN.toLowerCase())
  )
}
